/**
 * Client-side PREDICTED resource list for the Review step. This is not a
 * Terraform plan: nothing is planned or applied from the browser. Real plan
 * output arrives with the pipeline round. Resource names below are illustrative,
 * except the network, which follows the backend rule (revisions 3 and 4): Dedicated
 * creates rg-/vnet-/snet-<region>-<env>-ww-<customer> with the typed address ranges;
 * SaaS-Hybrid joins the chosen existing network, else the shared DB server's.
 * Every created server has the step-2 size and lists each of its data disks (R3-G); a dedicated
 * DB lists its SQL Server disks per role, <vm>-data-1, <vm>-log-1, <vm>-tempdb-1, numbered per role
 * (none for a TempDB on the local SSD).
 * Revision 8: no host name or TLS certificate; the shared gateway route has no name
 * of its own (the pipeline reports the application URL).
 *
 * Node-safe: relative imports only, no import.meta.
 */
import { isWindowsOs } from '../../config/workwise'
import type { DiskFormValue, PayloadDbDisk, WorkwiseFormValues, WorkwiseSku } from '../../types/workwise'
import type { SharedPicks, SizeInfo } from './cost'
import { flattenDbDisks } from './dbStorage'
import { formNetwork } from './model'

export type PreviewAction = 'create' | 'attach' | 'register'

export interface PreviewResource {
  type: string
  /** '' for a resource without a name of its own (the shared gateway route). */
  name: string
  action: PreviewAction
  note?: string
}

/** "name (note)", or "(note)" alone for a resource without a name. */
export function previewResourceText(resource: PreviewResource): string {
  return [resource.name, resource.note ? `(${resource.note})` : ''].filter(Boolean).join(' ')
}

function vmResources(vmName: string, windows: boolean, vmSize: string): PreviewResource[] {
  // vmSize is the admin VM size when known, otherwise the T-shirt tier.
  return [
    {
      type: windows ? 'azurerm_windows_virtual_machine' : 'azurerm_linux_virtual_machine',
      name: vmName || '(name pending)',
      action: 'create',
      note: vmSize,
    },
    { type: 'azurerm_network_interface', name: `nic-${vmName || 'pending'}`, action: 'create' },
  ]
}

/** One managed disk per entry of a server's "Data Disks" list. */
function diskResources(vmName: string, fallback: string, disks: DiskFormValue[]): PreviewResource[] {
  return disks.map((disk, i) => ({
    type: 'azurerm_managed_disk',
    name: `${vmName || fallback}-data-${i + 1}`,
    action: 'create',
    note: `${disk.sizeGb} GB ${disk.type}`,
  }))
}

/** One managed disk per disk of a dedicated DB's SQL Server storage, named <vm>-<role>-<n>, n counted per role. */
function dbDiskResources(vmName: string, disks: PayloadDbDisk[]): PreviewResource[] {
  const seen: Record<string, number> = {}
  return disks.map((disk) => {
    seen[disk.role] = (seen[disk.role] ?? 0) + 1
    return {
      type: 'azurerm_managed_disk',
      name: `${vmName || 'db'}-${disk.role}-${seen[disk.role]}`,
      action: 'create',
      note: `${disk.sizeGb} GB ${disk.type}`,
    }
  })
}

function networkResources(values: WorkwiseFormValues, picks: SharedPicks): PreviewResource[] {
  const network = formNetwork(values, picks)
  const range = (cidr: string) => (cidr ? { note: cidr } : {})
  if (network.source === 'customer') {
    return [
      { type: 'azurerm_resource_group', name: network.resourceGroup || 'rg-<region>-<env>-ww-<customer>', action: 'create' },
      { type: 'azurerm_virtual_network', name: network.vnet || 'vnet-<region>-<env>-ww-<customer>', action: 'create', ...range(network.vnetCidr) },
      { type: 'azurerm_subnet', name: network.subnet || 'snet-<region>-<env>-ww-<customer>', action: 'create', ...range(network.subnetCidr) },
    ]
  }
  if (network.existingNetwork) {
    return [
      {
        type: 'azurerm_subnet',
        name: network.subnet || '(subnet not recorded)',
        action: 'attach',
        note: `${network.vnet} in ${network.resourceGroup}, existing network ${network.existingNetwork.name}`,
      },
    ]
  }
  if (network.pending) {
    return [{ type: 'azurerm_subnet', name: 'network pending', action: 'attach', note: `not recorded for ${network.serverName} yet` }]
  }
  return [
    {
      type: 'azurerm_subnet',
      name: network.subnet || '(select a shared database server)',
      action: 'attach',
      note: network.vnet ? `${network.vnet} in ${network.resourceGroup}, from ${network.serverName}` : undefined,
    },
  ]
}

export function predictResources(
  values: WorkwiseFormValues,
  picks: SharedPicks = {},
  sizes?: Pick<SizeInfo, 'vmSizeFor'>,
): PreviewResource[] {
  const { appServer, dbServer, gatewayServer } = values
  const vmSize = (sku: WorkwiseSku) => sizes?.vmSizeFor(sku) ?? `size ${sku}`
  const out: PreviewResource[] = networkResources(values, picks)

  // App server: always dedicated. Dedicated DB and gateway share its step-2 size.
  const size = vmSize(appServer.sku)
  out.push(...vmResources(appServer.vmName, isWindowsOs(appServer.os), size))
  out.push(...diskResources(appServer.vmName, 'app', appServer.disks))

  if (values.deploymentModel === 'dedicated') {
    out.push(...vmResources(dbServer.vmName, true, size))
    out.push(...dbDiskResources(dbServer.vmName, flattenDbDisks(dbServer.storage)))
    out.push(...vmResources(gatewayServer.vmName, true, size))
    out.push(...diskResources(gatewayServer.vmName, 'gateway', gatewayServer.disks))
    if (gatewayServer.publicIp) {
      out.push({ type: 'azurerm_public_ip', name: `pip-${gatewayServer.vmName || 'gateway'}`, action: 'create' })
    }
    out.push({
      type: 'azurerm_network_security_group',
      name: `nsg-${gatewayServer.vmName || 'gateway'}`,
      action: 'create',
      note: `${gatewayServer.nsgRules.length} inbound rule(s)`,
    })
  } else {
    out.push({
      type: 'sql_database',
      name: '', // optional: Ansible names the database when it sets up the application
      action: 'attach',
      note: picks.db ? `on shared server ${picks.db.name}` : 'on the selected shared server',
    })
    out.push({
      type: 'gateway_route',
      name: '',
      action: 'register',
      note: picks.gateway ? `on shared gateway ${picks.gateway.name}` : 'on the selected shared gateway',
    })
  }

  return out
}
